import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, List, ListItem,
  ListItemIcon, ListItemText, Chip, Button, Divider, CircularProgress,
  Alert as MuiAlert, Tooltip
} from '@mui/material';
import {
  Notifications, NotificationsActive, NotificationsOff,
  Warning, Error, Info, CheckCircle, AccessTime, WaterDrop,
  Policy, Assignment, Restaurant, Cloud, Memory, Person,
  Schedule, Close, DoneAll, ChevronRight
} from '@mui/icons-material';
import { alertsAPI } from 'services/api';
import { useNavigate } from 'react-router-dom';

// Severity colors and icons
const SEVERITY_CONFIG = {
  critical: { color: 'error', icon: <Error />, bgColor: '#ffebee' },
  warning: { color: 'warning', icon: <Warning />, bgColor: '#fff3e0' },
  info: { color: 'info', icon: <Info />, bgColor: '#e3f2fd' }
};

// Alert type icons
const TYPE_ICONS = {
  doc_reminder: <Schedule />,
  weather_warning: <Cloud />,
  disease_alert: <Error />,
  feed_reminder: <Restaurant />,
  policy_expiry: <Policy />,
  claim_update: <Assignment />,
  water_quality: <WaterDrop />,
  mortality_alert: <Warning />,
  compliance: <CheckCircle />,
  general: <Info />
};

// Source icons
const SOURCE_ICONS = {
  manual: <Person />,
  weather: <Cloud />,
  ai: <Memory />,
  iot: <Memory />,
  system: <Info />,
  scheduler: <Schedule />
};

const AlertBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    unacknowledged: 0,
    critical: 0,
    warning: 0,
    info: 0,
    action_required: 0
  });
  const [error, setError] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [countRes, alertsRes] = await Promise.all([
        alertsAPI.getCount(),
        alertsAPI.getUnacknowledged(10)
      ]);
      
      setCounts(countRes.data);
      setAlerts(alertsRes.data || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
    
    // Poll for new alerts every 60 seconds
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    loadAlerts(); // Refresh on open
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAcknowledge = async (alertId) => {
    try {
      setAcknowledging(true);
      await alertsAPI.acknowledge(alertId);
      await loadAlerts();
    } catch (err) {
      setError('Failed to acknowledge alert');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      setAcknowledging(true);
      await alertsAPI.acknowledgeAll();
      await loadAlerts();
    } catch (err) {
      setError('Failed to acknowledge alerts');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleAlertClick = (alert) => {
    // Navigate to the specific pond dashboard if pond_id is available
    if (alert.pond_id) {
      navigate(`/farmer/ponds/${alert.pond_id}`);
      handleClose();
    } else if (alert.action_url) {
      navigate(alert.action_url);
      handleClose();
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return mins <= 0 ? 'Just now' : `${mins}m ago`;
    }
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}d ago`;
    }
    // Older
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const open = Boolean(anchorEl);
  const hasUnread = counts.unacknowledged > 0;
  const hasCritical = counts.critical > 0;

  return (
    <>
      {/* Alert Bell Button */}
      <Tooltip title={`${counts.unacknowledged} unread alerts`}>
        <IconButton
          onClick={handleOpen}
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <Badge 
            badgeContent={counts.unacknowledged} 
            color={hasCritical ? 'error' : 'warning'}
            max={99}
          >
            {hasUnread ? (
              <NotificationsActive sx={{ 
                animation: hasCritical ? 'shake 0.5s ease-in-out infinite' : 'none',
                '@keyframes shake': {
                  '0%, 100%': { transform: 'rotate(0deg)' },
                  '25%': { transform: 'rotate(-10deg)' },
                  '75%': { transform: 'rotate(10deg)' }
                }
              }} />
            ) : (
              <Notifications />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Alert Dropdown */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { 
            width: 380, 
            maxHeight: 500,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Alerts
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {counts.unacknowledged} unread • {counts.critical} critical
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {counts.unacknowledged > 0 && (
              <Tooltip title="Acknowledge All">
                <IconButton 
                  size="small" 
                  sx={{ color: 'white' }}
                  onClick={handleAcknowledgeAll}
                  disabled={acknowledging}
                >
                  <DoneAll />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" sx={{ color: 'white' }} onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Quick Stats */}
        {counts.unacknowledged > 0 && (
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            p: 1.5, 
            bgcolor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0'
          }}>
            {counts.critical > 0 && (
              <Chip 
                icon={<Error />} 
                label={`${counts.critical} Critical`} 
                color="error" 
                size="small"
              />
            )}
            {counts.warning > 0 && (
              <Chip 
                icon={<Warning />} 
                label={`${counts.warning} Warning`} 
                color="warning" 
                size="small"
              />
            )}
            {counts.action_required > 0 && (
              <Chip 
                icon={<AccessTime />} 
                label={`${counts.action_required} Action`} 
                color="primary" 
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <MuiAlert severity="error" sx={{ m: 2 }}>{error}</MuiAlert>
        ) : alerts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <NotificationsOff sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No new alerts
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You're all caught up!
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 350, overflow: 'auto' }}>
            {alerts.map((alert, index) => {
              const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
              const typeIcon = TYPE_ICONS[alert.alert_type] || TYPE_ICONS.general;
              
              return (
                <React.Fragment key={alert.id}>
                  <ListItem
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      bgcolor: config.bgColor,
                      cursor: (alert.pond_id || alert.action_url) ? 'pointer' : 'default',
                      '&:hover': { 
                        bgcolor: (alert.pond_id || alert.action_url) ? 'action.hover' : config.bgColor 
                      }
                    }}
                    onClick={() => (alert.pond_id || alert.action_url) && handleAlertClick(alert)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%',
                        bgcolor: `${config.color}.main`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        {React.cloneElement(typeIcon, { sx: { fontSize: 18 } })}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                            {alert.title}
                          </Typography>
                          {alert.action_required && (
                            <Chip 
                              label="Action" 
                              size="small" 
                              color="primary"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {alert.message}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mt: 0.5 
                          }}>
                            <Typography variant="caption" color="text.disabled">
                              {formatTime(alert.created_at)}
                            </Typography>
                            {alert.pond_name && (
                              <Chip 
                                icon={<WaterDrop sx={{ fontSize: '12px !important' }} />}
                                label={alert.pond_name}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Tooltip title="Acknowledge">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcknowledge(alert.id);
                          }}
                          disabled={acknowledging}
                          sx={{ p: 0.5 }}
                        >
                          <CheckCircle sx={{ fontSize: 18 }} color="success" />
                        </IconButton>
                      </Tooltip>
                      {(alert.pond_id || alert.action_url) && (
                        <ChevronRight sx={{ fontSize: 18, color: 'text.disabled' }} />
                      )}
                    </Box>
                  </ListItem>
                  {index < alerts.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}

        {/* Footer */}
        <Box sx={{ 
          p: 1.5, 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Button 
            size="small" 
            onClick={() => {
              navigate('/farmer/alerts');
              handleClose();
            }}
          >
            View All Alerts
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default AlertBell;

